import {
  Divider,
  LinearProgress,
  LinearProgressProps,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useMemo } from 'react';

import { AccountModel } from '@portal-clients/account';

import { BalanceIcon, WarningIcon } from '@frontend/components';
import { useLocale } from '@frontend/hooks';
import { useLicenses } from '@frontend/licenses';
import { tApp } from '@frontend/locales';
import { usePortalConfiguration } from '@frontend/utils';

export interface AccountDetailsBalanceProps {
  account: AccountModel;
}

interface BalanceColorModel {
  color: string;
  type: LinearProgressProps['color'];
}

const WARNING_PERCENTAGE_BREAKPOINT = 80;

const isInsufficient = (balance: number, limit: number): boolean => {
  return balance >= limit;
};

const isCloseToInsufficient = (balance: number, limit: number): boolean => {
  const percentage = calculatePercentage(balance, limit);
  return percentage >= WARNING_PERCENTAGE_BREAKPOINT && percentage < limit;
};

const calculatePercentage = (balance: number, limit: number): number => {
  return Math.min((balance / limit) * 100, 100);
};

export const AccountDetailsBalance = (props: AccountDetailsBalanceProps) => {
  const theme = useTheme();
  const { formatters } = useLocale();
  const {
    config: {
      features: { hidePrice: hidePriceFeature },
    },
  } = usePortalConfiguration();
  const { hidePrice } = useLicenses();

  const balance = useMemo(() => {
    if (props.account.type === 'DIRECT') return 0;

    if ((hidePrice && hidePriceFeature?.enabled) || !props.account.balance) {
      return undefined;
    }

    return Math.abs(props.account.balance);
  }, [props.account.id]);

  const balanceStatusColor: BalanceColorModel = useMemo(() => {
    if (!balance) {
      return { color: theme.palette.error.main, type: 'error' };
    }

    const { type } = props.account;

    if (type === 'CREDIT') {
      const { maxCreditLimit } = props.account;

      if (isInsufficient(balance, maxCreditLimit!)) {
        return { color: theme.palette.error.main, type: 'error' };
      }

      if (isInsufficient(balance, maxCreditLimit!)) {
        return { color: theme.palette.warning.main, type: 'warning' };
      }
    }

    if (type === 'PREPAID' && !balance) {
      return { color: theme.palette.error.main, type: 'error' };
    }

    return { color: theme.palette.success.main, type: 'success' };
  }, [props.account.id]);

  if (!hidePrice || !hidePriceFeature?.enabled || balance === undefined) {
    return null;
  }

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="column" spacing={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center">
            {props.account.type === 'CREDIT' && (
              <>
                {!isCloseToInsufficient(
                  balance,
                  props.account.maxCreditLimit!,
                ) &&
                  !isInsufficient(balance, props.account.maxCreditLimit!) && (
                    <BalanceIcon stroke={balanceStatusColor.color} />
                  )}

                {(isCloseToInsufficient(
                  balance,
                  props.account.maxCreditLimit!,
                ) ||
                  isInsufficient(balance, props.account.maxCreditLimit!)) && (
                  <WarningIcon stroke={balanceStatusColor.color} />
                )}
              </>
            )}
            {props.account.type === 'PREPAID' && (
              <>
                {balance ? (
                  <BalanceIcon stroke={balanceStatusColor.color} />
                ) : (
                  <WarningIcon stroke={balanceStatusColor.color} />
                )}
              </>
            )}
            <Typography
              color={balanceStatusColor.color}
              variant="subtitle1"
              ml={0.5}
            >
              {props.account.type === 'CREDIT'
                ? tApp.accountCard.credit()
                : tApp.accountCard.balance()}
            </Typography>
          </Stack>

          <Typography color={balanceStatusColor.color} variant="subtitle1">
            {formatters.currency(balance)}
          </Typography>
        </Stack>
        {props.account.type === 'CREDIT' && (
          <>
            <LinearProgress
              sx={{
                height: 8,
                borderRadius: 24,
                backgroundColor: theme.palette.grey[300],
              }}
              color={balanceStatusColor.type}
              variant="determinate"
              value={calculatePercentage(
                balance,
                props.account.maxCreditLimit!,
              )}
              valueBuffer={100}
            />
            <Stack direction="row">
              <Typography variant="body2" color="textSecondary">
                {formatters.currency(props.account.maxCreditLimit!)}
              </Typography>
            </Stack>
          </>
        )}
      </Stack>
      <Divider />
    </Stack>
  );
};
